import classNames from "classnames";
import React from "react";

import { LeaderboardPage } from "lib/types/leaderboards";
import { TRANSPARENT_PIXEL } from "lib/constants/shared";
import { getDateFormattedNumeric } from "lib/utils";

import { MarkdownTracked } from "components/markdownTracked/markdownTracked";
import IconButton from "components/iconButton/iconButton";
import { BackArrowIcon } from "components/icons/icons";
import Image from "components/imageNew/image";

type DetailHeroProps = {
  title: string;
  subtitle?: string;
  data: LeaderboardPage;
  onTrackMixpanel: (
    event: string,
    data: {
      [key: string]: string;
    }
  ) => Promise<void>;
  backLink?: {
    href: string;
    label: string;
    ariaLabel: string;
  };
  mixpanelEventType?: MixpanelEventType;
  showPublishedDate?: boolean;
};

type MixpanelEventType = "Leaderboard" | "Ranking";

const DetailHero = ({
  title,
  data,
  onTrackMixpanel,
  backLink,
  mixpanelEventType = "Leaderboard",
  showPublishedDate = true,
}: DetailHeroProps) => {
  return (
    <header className={"text-black-rock"}>
      <div className={"pt-[40px]"}>
        <div className={"mb-5 flex items-center justify-between"}>
          <div className={"flex items-center"}>
            <IconButton
              className={"[&_path]:fill-current [&_svg]:p-[4px]"}
              component={"link"}
              aria-label={backLink?.ariaLabel || "Go to leaderboards"}
              href={backLink?.href || "/leaderboards"}
            >
              <BackArrowIcon width={24} height={24} />
            </IconButton>
            {backLink?.label && (
              <a
                href={backLink?.href}
                className={"ml-1 font-bold text-[16px] text-black-rock"}
              >
                {backLink.label}
              </a>
            )}
          </div>
          {showPublishedDate && (
            <div className={"flex items-center justify-end"}>
              <div className={"mr-5 flex text-[16px] leading-[130%]"}>
                {data.editionLabel && (
                  <>
                    <span className={"text-cerise"}>{data.editionLabel}</span>
                    <span className={"flex items-center px-[10px]"}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="4"
                        height="5"
                        viewBox="0 0 4 5"
                        fill="none"
                      >
                        <circle cx="2" cy="2.5" r="2" fill="#AAAAB2" />
                      </svg>
                    </span>
                  </>
                )}
                <span>{getDateFormattedNumeric(data.publishDate)}</span>
              </div>
              <IconButton
                component={"link"}
                className={
                  "text-black-rock [&_path]:fill-current [&_svg]:p-[2px]"
                }
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                  data.title + ` https://sifted.eu/leaderboards/${data.slug}`
                )}`}
                aria-label={"Share to X"}
                target={"_blank"}
                onClick={async () => {
                  await onTrackMixpanel("Leaderboard share X", {
                    Component: "Leaderboard Page Hero",
                    "Click link": `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                      data.title +
                        ` https://sifted.eu/leaderboards/${data.slug}`
                    )}`,
                    "Click text": "Share to X",
                  });
                }}
              >
                <span
                  className={"flex h-100 w-100 items-center justify-center"}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="19"
                    viewBox="0 0 18 19"
                    fill="none"
                  >
                    <path
                      d="M10.7124 8.0452L17.4133 0H15.8255L10.0071 6.98554L5.35992 0H0L7.02738 10.5634L0 19H1.58799L7.73237 11.623L12.6401 19H18L10.7121 8.0452H10.7124ZM8.53747 10.6564L7.82546 9.60456L2.16016 1.2347H4.59922L9.17118 7.98944L9.8832 9.04132L15.8262 17.8215H13.3871L8.53747 10.6568V10.6564Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
              </IconButton>
            </div>
          )}
        </div>
        <h1
          className={classNames(
            "mb-3 font-recklessBold text-[32px] leading-[130%] text-black-rock md:text-[52px]"
          )}
        >
          {title}
        </h1>
        {(data as any).subtitle ? (
          <p className="mt-md pb-6 leading-[130%] md:text-[24px] lg:mt-xl lg:leading-snug ">
            {(data as any).subtitle}
          </p>
        ) : null}
        {data.description && (
          <div
            className={"mb-5 text-[16px]"}
            data-testid={"detailHero__description"}
          >
            <MarkdownTracked
              text={data.description}
              component="Leaderboard Page Hero"
            />
          </div>
        )}
        {data.sponsors.length > 0 && (
          <ul className={"flex gap-5"} aria-label={"Sponsors"}>
            {data.sponsors.map((sponsor) => {
              return (
                <li key={sponsor.id} className={"flex flex-col gap-y-[6px]"}>
                  <p className={"text-[14px] leading-[130%] text-mono-30"}>
                    {sponsor.sponsorshipType}
                  </p>
                  {sponsor.logo && sponsor.url && (
                    <a
                      href={sponsor.url}
                      target={"_blank"}
                      rel="noopener noreferrer"
                      className={
                        "focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cerise"
                      }
                      aria-label={`${sponsor.name} website`}
                      onClick={async () => {
                        await onTrackMixpanel(
                          `${mixpanelEventType} sponsor click`,
                          {
                            Component: `${mixpanelEventType} Page Hero`,
                            "Click link": sponsor.url as string,
                            "Click text": `${sponsor.name} website`,
                          }
                        );
                      }}
                    >
                      <Image
                        className="h-[48px] w-[120px] object-contain object-center"
                        src={sponsor.logo.url}
                        width={sponsor.logo.width}
                        height={sponsor.logo.height}
                        alt={sponsor.logo.alt || `${sponsor.name} logo`}
                        blurDataURL={
                          sponsor.logo.blurUpThumb || TRANSPARENT_PIXEL
                        }
                        sizes={"200px"}
                      />
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </header>
  );
};

export default DetailHero;
